import { useCallback, useEffect, useState } from 'react';
import {
  ConversationVariables,
  ConversationVariableUpdateRequest,
} from 'Assistant/__generated__/client.schemas';
import useFeatureFlag from 'flags/useFeatureFlag';
import { logException } from 'App/logging';
import {
  getConversationVariablesConversationVariablesGet as getConversationVariables,
  updateConversationVariablesConversationVariablesPatch as updateConversationVariables,
} from 'Assistant/__generated__/client';
import { useAuthHeader } from './useAuthHeader';

type ErrorState = { type: 'fetch' } | { type: 'update' } | null;

type InvocationState = {
  isLoading: boolean;
  error: ErrorState;
};

type DataInvocationState<T> = InvocationState & {
  data: T | null;
};

type UseConversationVariablesResponse = {
  isLoading: boolean;
  conversationVariables: ConversationVariables | null;
  error: ErrorState;
  setConversationVariables(
    request: ConversationVariableUpdateRequest,
  ): Promise<boolean>;
};

function useConversationVariables(): UseConversationVariablesResponse {
  const enabled = useFeatureFlag('nova-conversation-styles-enabled');
  const accessTokenHeader = useAuthHeader();
  const [conversationVariableState, setConversationVariableState] = useState<
    DataInvocationState<ConversationVariables>
  >({
    isLoading: true,
    error: null,
    data: null,
  });

  const fetchConversationStyle = useCallback(async () => {
    if (!accessTokenHeader) return;

    setConversationVariableState(prevState => ({
      ...prevState,
      isLoading: true,
      error: null,
    }));

    try {
      const { data, status } = await getConversationVariables({
        headers: {
          'client-platform': 'web',
          ...accessTokenHeader,
        },
      });

      if (status !== 200) {
        throw new Error(
          `Error while fetching conversation style, status ${status}`,
        );
      }

      setConversationVariableState(prevState => ({
        ...prevState,
        isLoading: false,
        data,
        error: null,
      }));
    } catch (error) {
      setConversationVariableState(prevState => ({
        ...prevState,
        isLoading: false,
        error: { type: 'fetch' },
      }));

      logException(error, {
        tags: {
          product: 'nova',
          feature: 'nova-chat',
          action: 'fetch-conversation-variables',
        },
        extra: {
          hasAuthHeader: !!accessTokenHeader,
          enabled,
        },
      });
    }
  }, [accessTokenHeader, enabled]);

  const setConversationVariables = useCallback(
    async (request: ConversationVariableUpdateRequest) => {
      if (!accessTokenHeader) return false;

      setConversationVariableState(prevState => ({
        ...prevState,
        isLoading: true,
        error: null,
      }));

      try {
        const { status } = await updateConversationVariables(request, {
          headers: {
            'client-platform': 'web',
            ...accessTokenHeader,
          },
        });

        if (status !== 200) {
          throw new Error(
            `Error while updating conversation style, status ${status}`,
          );
        }

        setConversationVariableState(prevState => ({
          ...prevState,
          isLoading: false,
          data: {
            ...prevState.data,
            conversation_style:
              request.conversation_style || prevState.data?.conversation_style,
            topics: request.topics || prevState.data?.topics,
          },
          error: null,
        }));

        return true;
      } catch (error) {
        const currentData = conversationVariableState.data;

        setConversationVariableState(prevState => ({
          ...prevState,
          isLoading: false,
          error: {
            type: 'update',
          },
        }));

        logException(error, {
          tags: {
            product: 'nova',
            feature: 'nova-chat',
            action: 'update-conversation-variables',
          },
          extra: {
            hasAuthHeader: !!accessTokenHeader,
            conversationStyle: request.conversation_style,
            topics: request.topics,
            currentData,
          },
        });

        return false;
      }
    },
    [accessTokenHeader, conversationVariableState.data],
  );

  useEffect(() => {
    if (enabled) {
      void fetchConversationStyle();
    } else {
      setConversationVariableState({
        isLoading: false,
        error: null,
        data: null,
      });
    }
  }, [enabled, fetchConversationStyle]);

  return {
    isLoading: conversationVariableState.isLoading,
    conversationVariables: conversationVariableState.data,
    error: conversationVariableState.error,
    setConversationVariables,
  };
}

export { useConversationVariables };
export type { UseConversationVariablesResponse };
